// lib/qwen-image-3.ts
import type { QwenImage3Resolution } from '#types/types';

/** fal accepts 3 images on /edit, the edit source included. */
export const QWEN_IMAGE_3_MAX_INPUT_IMAGES = 3;
// Per-image and per-side provider limits; a side outside them is rejected before generation.
export const QWEN_IMAGE_3_MIN_SIDE = 384;
export const QWEN_IMAGE_3_MAX_SIDE = 2048;
// The provider's own ceiling and floor on the total, whatever the tier asked for.
export const QWEN_IMAGE_3_MAX_PIXELS = 2048 * 2048;
export const QWEN_IMAGE_3_MIN_PIXELS = 512 * 512;
export const QWEN_IMAGE_3_MAX_PROMPT_CHARS = 5000;
export const QWEN_IMAGE_3_MAX_NEGATIVE_PROMPT_CHARS = 500;
export const QWEN_IMAGE_3_DEFAULT_RESOLUTION: QwenImage3Resolution = '2K';

// fal bills $0.04 at 1K and $0.075 at 2K without documenting where the cut falls. 1 Mpx is our
// assumption, to be confirmed against the first real invoice.
// What each tier buys, which is not the same statement as the provider ceiling above even though
// 2K happens to reach it today.
export const QWEN_IMAGE_3_RESOLUTION_PIXELS: Record<QwenImage3Resolution, number> = {
  '1K': 1024 * 1024,
  '2K': 2048 * 2048,
};

export const resolveQwenImage3Resolution = (value: unknown): QwenImage3Resolution | null => {
  const resolution = typeof value === 'string' ? value.trim().toUpperCase() : value;
  switch (resolution) {
    case undefined:
    case null:
    case '':
      return QWEN_IMAGE_3_DEFAULT_RESOLUTION;
    case '1K':
    case '2K':
      return resolution;
    default:
      return null;
  }
};

export const getQwenImage3Endpoint = (hasSource: boolean): string => (
  `alibaba/qwen-image-3/${hasSource ? 'edit' : 'text-to-image'}`
);

// The only shapes this cannot keep are the ones past 5.33:1, where the provider's own box leaves
// no size that holds both the ratio and the per-side floor.
const fitToProviderBox = (width: number, height: number, budgetPixels: number, allowUpscale: boolean) => {
  const baseWidth = width > 0 ? width : 1;
  const baseHeight = height > 0 ? height : 1;
  const fit = Math.min(
    Math.sqrt(budgetPixels / (baseWidth * baseHeight)),
    QWEN_IMAGE_3_MAX_SIDE / Math.max(baseWidth, baseHeight),
  );
  // Raising a short side by clamping it alone would stretch the image, so the whole shape grows
  // until the short side clears the floor; the long side is capped afterwards.
  const scale = Math.max(
    allowUpscale ? fit : Math.min(1, fit),
    QWEN_IMAGE_3_MIN_SIDE / Math.min(baseWidth, baseHeight),
  );
  const clampSide = (value: number) => Math.min(QWEN_IMAGE_3_MAX_SIDE, Math.max(QWEN_IMAGE_3_MIN_SIDE, value));
  // The epsilon absorbs float noise, so an exact 2048 does not floor to 2047.
  let fitted = {
    width: clampSide(Math.floor(baseWidth * scale + 1e-9)),
    height: clampSide(Math.floor(baseHeight * scale + 1e-9)),
  };
  // The per-side floor alone does not reach the provider's total-pixel floor: a shape that misses
  // it is grown until it does, because fal rejects the request rather than upscaling for us.
  if (fitted.width * fitted.height < QWEN_IMAGE_3_MIN_PIXELS) {
    const bump = Math.sqrt(QWEN_IMAGE_3_MIN_PIXELS / (fitted.width * fitted.height));
    fitted = {
      width: clampSide(Math.ceil(fitted.width * bump)),
      height: clampSide(Math.ceil(fitted.height * bump)),
    };
  }
  return fitted;
};

/** The geometry we send and the geometry we ask back: one shape, the source ratio at the paid tier. */
export const resolveQwenImage3Dimensions = ({
  sourceWidth,
  sourceHeight,
  resolution,
}: {
  sourceWidth: number;
  sourceHeight: number;
  resolution: unknown;
}): { width: number; height: number } => fitToProviderBox(
  sourceWidth,
  sourceHeight,
  QWEN_IMAGE_3_RESOLUTION_PIXELS[resolveQwenImage3Resolution(resolution) ?? QWEN_IMAGE_3_DEFAULT_RESOLUTION],
  true,
);

/** A Canvas task's geometry: the provider dimensions, and the factor that maps them back onto the Generation Area. */
export const resolveQwenImage3TaskGeometry = (
  generationRect: { w: number; h: number },
  resolution: unknown,
): { width: number; height: number; upscaleFactor: number } => {
  const { width, height } = resolveQwenImage3Dimensions({ sourceWidth: generationRect.w, sourceHeight: generationRect.h, resolution });
  return { width, height, upscaleFactor: generationRect.w > 0 ? width / generationRect.w : 1 };
};

/**
 * The trust boundary for a geometry that arrives with the task: shrunk into the tier the run is
 * billed for, never enlarged past what the input carries.
 */
export const resolveQwenImage3ImageSize = (
  size: { width?: number | null; height?: number | null },
  resolution: unknown,
): { width: number; height: number } => fitToProviderBox(
  typeof size.width === 'number' && Number.isFinite(size.width) ? size.width : 0,
  typeof size.height === 'number' && Number.isFinite(size.height) ? size.height : 0,
  QWEN_IMAGE_3_RESOLUTION_PIXELS[resolveQwenImage3Resolution(resolution) ?? QWEN_IMAGE_3_DEFAULT_RESOLUTION],
  false,
);

/** A reference image is not billed by size: it only has to clear the provider's per-side window. */
export const fitQwenImage3Reference = (
  width: number,
  height: number,
): { width: number; height: number } => fitToProviderBox(width, height, QWEN_IMAGE_3_MAX_PIXELS, false);

export const buildQwenImage3ProviderInput = (input: {
  prompt: string;
  resolution: unknown;
  imageSize: { width: number; height: number };
  negativePrompt?: string;
  seed?: number;
  imageUrls?: readonly string[];
}) => {
  const imageUrls = (input.imageUrls ?? [])
    .filter((url, index, urls): url is string => typeof url === 'string' && url.trim().length > 0 && urls.indexOf(url) === index)
    .slice(0, QWEN_IMAGE_3_MAX_INPUT_IMAGES);
  const negativePrompt = (input.negativePrompt ?? '').trim().slice(0, QWEN_IMAGE_3_MAX_NEGATIVE_PROMPT_CHARS);
  const seed = typeof input.seed === 'number' && Number.isFinite(input.seed) && input.seed >= 0
    ? Math.min(2147483647, Math.floor(input.seed))
    : undefined;
  return {
    prompt: input.prompt.slice(0, QWEN_IMAGE_3_MAX_PROMPT_CHARS),
    image_size: resolveQwenImage3ImageSize(input.imageSize, input.resolution),
    // Studio owns batching (one task per variant), so the provider always returns a single image.
    num_images: 1,
    // The provider's expansion rewords the assembled instruction and drifts quoted strings.
    enable_prompt_expansion: false,
    // Disabling the checker needs an account authorization we do not have.
    enable_safety_checker: true,
    // A data-URI response would blow up the fal.subscribe payload.
    sync_mode: false,
    // The output is composited back onto the canvas and re-edited, so a lossy re-encode would compound.
    output_format: 'png' as const,
    ...(negativePrompt ? { negative_prompt: negativePrompt } : {}),
    ...(seed === undefined ? {} : { seed }),
    // Order is load-bearing: the prompt addresses these as "image 1", "image 2", "image 3".
    ...(imageUrls.length > 0 ? { image_urls: imageUrls } : {}),
  };
};
